import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiX } from 'react-icons/fi';
import { CoachCard, Badge, Spinner, EmptyState, Button } from '../components/Components';
import { coachAPI, miscAPI } from '../utils/api';
import './Search.css';

export default function Search() {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState('');
  const [expertise, setExpertise] = useState('');
  const [sort, setSort] = useState('rating');

  useEffect(() => {
    miscAPI.getExpertise().then(r => setAreas(r.data.areas)).catch(() => {});
  }, []);

  const loadCoaches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await coachAPI.search({ search, expertise, sort, page, limit: 12 });
      setCoaches(res.data.coaches);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, expertise, sort, page]);

  useEffect(() => { loadCoaches(); }, [loadCoaches]);

  // Debounce search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const quickFilters = ['Career Development', 'Software Engineering', 'Leadership & Management', 'Life Coaching', 'Data Science & AI', 'Public Speaking'];

  return (
    <div className="page-container">
      <div className="search-header">
        <h2 className="search-title">Find Your Coach</h2>
        <p className="search-subtitle">Search by name, expertise, or area of interest</p>
      </div>

      {/* Search & Filters */}
      <div className="search-filters card">
        <div className="search-filters__row">
          <div className="search-filters__input-wrap">
            <FiSearch className="search-filters__icon" />
            <input
              className="search-filters__input"
              placeholder="Search by name, keyword, expertise..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button className="search-filters__clear" onClick={() => setSearchInput('')}><FiX size={16} /></button>
            )}
          </div>
          <div className="search-filters__select-wrap">
            <FiFilter className="search-filters__icon" />
            <select
              className="search-filters__select"
              value={expertise}
              onChange={(e) => { setExpertise(e.target.value); setPage(1); }}
            >
              <option value="">All Areas</option>
              {areas.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="search-filters__select-wrap search-filters__select-wrap--small">
            <select
              className="search-filters__select"
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
            >
              <option value="rating">Top Rated</option>
              <option value="sessions">Most Sessions</option>
              <option value="rate_low">Price: Low → High</option>
              <option value="rate_high">Price: High → Low</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="search-quick-filters">
        {quickFilters.map(a => (
          <Badge
            key={a} variant="primary"
            selected={expertise === a}
            onClick={() => { setExpertise(expertise === a ? '' : a); setPage(1); }}
          >
            {a}
          </Badge>
        ))}
      </div>

      {/* Results count */}
      <div className="search-results-count">
        {total} coach{total !== 1 ? 'es' : ''} found
      </div>

      {/* Coach List */}
      {loading ? (
        <Spinner />
      ) : coaches.length === 0 ? (
        <EmptyState
          icon="🔍"
          title="No coaches found"
          description="Try adjusting your search filters"
          action={
            <Button variant="secondary" onClick={() => { setSearchInput(''); setExpertise(''); }}>
              Clear Filters
            </Button>
          }
        />
      ) : (
        <div className="search-results">
          {coaches.map(coach => (
            <CoachCard
              key={coach._id}
              coach={coach}
              onViewProfile={(id) => navigate(`/coach/${id}`)}
              onBookSession={(id) => navigate(`/book/${id}`)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="search-pagination">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</Button>
          <span className="search-pagination__info">Page {page} of {totalPages}</span>
          <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</Button>
        </div>
      )}
    </div>
  );
}
